import React, { useEffect, useState } from "react";
import { Modal, Descriptions, Empty, Tag, Space, Divider, Typography, Tooltip, Alert, Spin } from "antd";
import { ClockCircleOutlined, GlobalOutlined, WarningOutlined } from "@ant-design/icons";
import { useTimeZone, TIMEZONE_OPTIONS } from "../../../../contexts/TimeZoneContext";
import useTimezoneConverter from "../../../../hooks/useTimezoneConverter";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import AircraftTag from "../../../../components/AircraftTag";
// 扩展 dayjs 插件
dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;

// 星期映射表
const WEEKDAY_MAP = {
    MON: "周一",
    TUE: "周二",
    WED: "周三",
    THU: "周四",
    FRI: "周五",
    SAT: "周六",
    SUN: "周日",
};

/**
 * 宵禁规则查看组件
 * @param {Object} props
 * @param {boolean} props.visible - 是否显示对话框
 * @param {Function} props.onClose - 关闭对话框回调
 * @param {Object} props.airport - 机场信息对象
 */
const CurfewRulesViewer = ({ visible, onClose, airport }) => {
    const { timeZone } = useTimeZone(); // 使用时区上下文
    const [loading, setLoading] = useState(false);
    const [convertError, setConvertError] = useState(null);

    // 使用统一的时区转换钩子
    const { fromUTC, formatWithDayMark, getTimezoneInfo } = useTimezoneConverter(airport?.iata_code, { [airport?.iata_code]: airport });

    // 检查规则的有效性并记录问题
    useEffect(() => {
        if (visible && airport) {
            setLoading(true);
            setConvertError(null);

            try {
                const validateRules = (rules, type) => {
                    if (!Array.isArray(rules)) return;

                    rules.forEach((rule, index) => {
                        if (!rule.start || !rule.end) {
                            console.warn(`${type} 宵禁规则 #${index + 1} 缺少开始或结束时间:`, rule);
                        } else {
                            // 检查24:00特殊格式
                            let startValid = true,
                                endValid = true;

                            // 标准时间格式正则
                            const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
                            // 24:00特殊格式正则
                            const time24Regex = /^24:00(:00)?$/;

                            // 分别验证开始和结束时间
                            startValid = timeRegex.test(rule.start) || time24Regex.test(rule.start);
                            endValid = timeRegex.test(rule.end) || time24Regex.test(rule.end);

                            if (!startValid || !endValid) {
                                console.warn(`${type} 宵禁规则 #${index + 1} 时间格式无效:`, `start=${rule.start}(${startValid}), end=${rule.end}(${endValid})`);
                            }
                        }
                    });
                };

                validateRules(airport.dep_curfew_rules, "起飞");
                validateRules(airport.arr_curfew_rules, "降落");

                // 检查机场时区信息
                if (!airport.timezone) {
                    console.warn(`机场 ${airport.iata_code} 缺少时区信息`);
                }
            } catch (error) {
                console.error("验证规则时出错:", error);
                setConvertError("数据验证失败，请检查控制台获取详细信息");
            } finally {
                setLoading(false);
            }
        }
    }, [visible, airport, timeZone]);

    if (!airport) {
        return null;
    }

    const { iata_code, airport_name, timezone, dep_curfew_rules = [], arr_curfew_rules = [] } = airport;
    const hasDepRules = dep_curfew_rules && dep_curfew_rules.length > 0;
    const hasArrRules = arr_curfew_rules && arr_curfew_rules.length > 0;
    const hasNoRules = !hasDepRules && !hasArrRules;

    // 格式化适用时间
    const formatApplyTime = (rule) => {
        const type = rule.type || "daily";

        if (type === "daily") {
            return "每天";
        } else if (type === "weekly") {
            if (!rule.weekdays || rule.weekdays.length === 0) {
                return "每周（未指定天数）";
            }
            // 返回weekdays的格式化字符串
            const weekdaysStr = rule.weekdays.map((day) => WEEKDAY_MAP[day] || day).join("、");

            // 添加有效期显示
            if (rule.valid_from && rule.valid_to) {
                return `${weekdaysStr}（有效期：${rule.valid_from} 至 ${rule.valid_to}）`;
            }
            return weekdaysStr;
        } else if (type === "specific") {
            if (!rule.date_start || !rule.date_end) {
                return "指定日期（未设置）";
            }
            return `${rule.date_start} 至 ${rule.date_end}`;
        }
        return "未知类型";
    };

    // 安全转换时间函数 - 捕获异常
    const safeConvertTime = (timeStr) => {
        try {
            if (!timeStr) {
                return "-";
            }

            const result = handleConvertTime(timeStr);

            // 检查结果是否是有效的时间
            if (result === "Invalid Date" || !result) {
                console.warn(`时间转换结果无效:`, { input: timeStr, output: result });
                setConvertError(`转换时间 ${timeStr} 失败，返回了无效结果`);
                return timeStr; // 返回原始字符串
            }

            return result;
        } catch (error) {
            console.error(`转换时间 ${timeStr} 失败:`, error);
            setConvertError(`转换时间 ${timeStr} 失败，请检查时区配置`);
            return timeStr;
        }
    };

    // 转换时间函数 - 使用公共时区转换钩子
    const handleConvertTime = (timeStr) => {
        if (!timeStr || typeof timeStr !== "string") {
            console.warn("无效的时间字符串:", timeStr);
            return "-";
        }

        // 宵禁规则特殊情况处理 - 通常为"HH:MM"或"HH:MM:SS"格式
        // 这种情况不能直接用fromUTC处理，需要特殊处理

        // 检查是否是"24:00"特殊格式
        if (timeStr === "24:00" || timeStr === "24:00:00") {
            return "00:00";
        }

        // 检查是否是简单的时间格式（不包含日期部分）
        const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])(:([0-5][0-9]))?$/;
        if (timeRegex.test(timeStr)) {
            // 对于简单时间格式，我们需要构造一个完整的日期时间字符串
            // 使用2000-01-01作为基准日期(实际日期不重要，因为只关心时间部分)
            const fullDateTimeStr = `2000-01-01T${timeStr.length <= 5 ? timeStr + ":00" : timeStr}Z`;

            try {
                // 使用dayjs直接处理时区转换
                const utcTime = dayjs.utc(fullDateTimeStr);
                if (!utcTime.isValid()) {
                    console.warn("无效的时间字符串:", timeStr, fullDateTimeStr);
                    return timeStr;
                }

                // 根据当前选择的时区转换
                let localTime;
                if (timeZone === TIMEZONE_OPTIONS.UTC) {
                    // UTC时区直接返回，但只保留时间部分
                    return utcTime.format("HH:mm");
                } else if (timeZone === TIMEZONE_OPTIONS.BEIJING) {
                    // 北京时间 (UTC+8)
                    localTime = utcTime.tz("Asia/Shanghai");
                } else if (timeZone === TIMEZONE_OPTIONS.LOCAL && iata_code && airport) {
                    // 机场当地时区
                    const airportTimezone = airport.timezone;
                    if (!airportTimezone) {
                        console.warn(`机场 ${iata_code} 缺少时区信息，降级到UTC`);
                        return timeStr;
                    }
                    localTime = utcTime.tz(airportTimezone);
                } else {
                    // 默认使用UTC
                    return timeStr;
                }

                // 只返回时间部分 HH:MM
                return localTime.format("HH:mm");
            } catch (error) {
                console.error("转换时间出错:", error, { timeStr, timeZone });
                return timeStr;
            }
        }

        // 对于其他格式，尝试使用fromUTC
        try {
            return fromUTC(timeStr, "HH:mm");
        } catch (error) {
            console.error("转换时间出错:", error, { timeStr });
            return timeStr;
        }
    };

    // 获取当前使用的时区标识
    const getTimezoneLabel = () => {
        switch (timeZone) {
            case TIMEZONE_OPTIONS.BEIJING:
                return "UTC+8";
            case TIMEZONE_OPTIONS.LOCAL:
                return iata_code;
            default:
                return "UTC";
        }
    };

    // 获取当前使用的时区描述和偏移量 - 使用钩子提供的方法
    const getTimezoneDisplayInfo = () => {
        if (timeZone === TIMEZONE_OPTIONS.UTC) {
            return "世界标准时间(UTC)";
        }

        // getTimezoneInfo返回的是一个包含时区信息的对象，不是字符串
        const tzInfo = getTimezoneInfo();

        let label;
        if (tzInfo.isBeijing) {
            label = "北京时间";
        } else if (tzInfo.isLocal && tzInfo.usingAirport) {
            label = `${tzInfo.usingAirport}当地时间`;
        } else {
            return tzInfo.actual || "未知时区";
        }

        // 如果tzInfo中已经包含了偏移量显示，直接使用
        if (tzInfo.offsetDisplay) {
            return `${label} (${tzInfo.offsetDisplay})`;
        }

        // 如果没有，尝试使用dayjs计算（这是备用方案）
        try {
            const now = dayjs().tz(tzInfo.actual);
            const offsetMinutes = now.utcOffset();
            const hours = Math.floor(Math.abs(offsetMinutes) / 60);
            const minutes = Math.abs(offsetMinutes) % 60;

            const sign = offsetMinutes >= 0 ? "+" : "-";
            const minuteStr = minutes > 0 ? `:${minutes.toString().padStart(2, "0")}` : "";

            const offset = `UTC${sign}${hours}${minuteStr}`;
            return `${label} (${offset})`;
        } catch (e) {
            console.error("计算时区偏移量出错:", e);
            return label;
        }
    };

    // 检查是否有无效的宵禁规则
    const hasInvalidRules = () => {
        const checkRules = (rules) => {
            if (!Array.isArray(rules)) return false;
            return rules.some((rule) => !rule.start || !rule.end);
        };

        return checkRules(dep_curfew_rules) || checkRules(arr_curfew_rules);
    };

    return (
        <Modal
            title={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{`${airport_name} (${iata_code}) 宵禁规则`}</span>
                    {timeZone !== TIMEZONE_OPTIONS.UTC && (
                        <Tooltip title={getTimezoneDisplayInfo()}>
                            <Tag icon={<GlobalOutlined />} color="blue">
                                {getTimezoneLabel()}
                            </Tag>
                        </Tooltip>
                    )}
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={600}
            className="curfew-rules__viewer"
        >
            {loading ? (
                <div style={{ textAlign: "center", padding: "30px 0" }}>
                    <Spin tip="加载宵禁规则..." />
                </div>
            ) : (
                <>
                    {convertError && <Alert message="时区转换错误" description={convertError} type="error" showIcon style={{ marginBottom: 16 }} />}

                    {hasInvalidRules() && <Alert message="数据警告" description="部分宵禁规则数据可能不完整或格式异常，显示可能不准确。" type="warning" showIcon icon={<WarningOutlined />} style={{ marginBottom: 16 }} />}

                    {hasNoRules ? (
                        <Empty description="该机场没有设置宵禁规则" />
                    ) : (
                        <div>
                            {hasDepRules && (
                                <div className="rule-section">
                                    <Title level={5} className="curfew-rules__title">
                                        <ClockCircleOutlined style={{ color: "#f5222d" }} />
                                        起飞宵禁规则
                                    </Title>

                                    {dep_curfew_rules.map((rule, index) => (
                                        <div key={`dep-${index}`} style={{ marginBottom: 16 }}>
                                            <Descriptions bordered size="small" column={1}>
                                                <Descriptions.Item label="适用时间(UTC)">
                                                    <Tag color="cyan">{formatApplyTime(rule)}</Tag>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="宵禁时段(UTC)">
                                                    <Tag className={hasDepRules ? "tag-red" : "tag-orange"}>
                                                        {safeConvertTime(rule.start)} 至 {safeConvertTime(rule.end)}
                                                    </Tag>
                                                    {(!rule.start || !rule.end || rule.start === "Invalid Date" || rule.end === "Invalid Date") && (
                                                        <Tooltip title="宵禁时间数据格式可能有问题">
                                                            <Tag color="orange" style={{ marginLeft: 8 }}>
                                                                <WarningOutlined /> 时间格式问题
                                                            </Tag>
                                                        </Tooltip>
                                                    )}
                                                </Descriptions.Item>
                                                {/* 原始时间数据 - 仅在开发环境下显示
                                                {process.env.NODE_ENV === "development" && (
                                                    <Descriptions.Item label="原始时间数据">
                                                        <small style={{ color: "#999" }}>
                                                            开始: {rule.start}, 结束: {rule.end}
                                                        </small>
                                                    </Descriptions.Item>
                                                )}
                                                */}
                                                <Descriptions.Item label="例外飞机">
                                                    {rule.exceptions && rule.exceptions.length > 0 ? (
                                                        <Space wrap className="curfew-rules__exceptions">
                                                            {rule.exceptions.map((reg) => (
                                                                <AircraftTag key={reg} registration={reg} />
                                                            ))}
                                                        </Space>
                                                    ) : (
                                                        <Text type="secondary">无例外飞机</Text>
                                                    )}
                                                </Descriptions.Item>
                                            </Descriptions>
                                            {index < dep_curfew_rules.length - 1 && <Divider dashed style={{ margin: "12px 0" }} />}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {hasArrRules && (
                                <div className="rule-section">
                                    <Title level={5} className="curfew-rules__title">
                                        <ClockCircleOutlined style={{ color: "#fa8c16" }} />
                                        降落宵禁规则
                                    </Title>

                                    {arr_curfew_rules.map((rule, index) => (
                                        <div key={`arr-${index}`} style={{ marginBottom: 16 }}>
                                            <Descriptions bordered size="small" column={1}>
                                                <Descriptions.Item label="适用时间(UTC)">
                                                    <Tag color="cyan">{formatApplyTime(rule)}</Tag>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="宵禁时段(UTC)">
                                                    <Tag className={hasArrRules ? "tag-red" : "tag-orange"}>
                                                        {safeConvertTime(rule.start)} 至 {safeConvertTime(rule.end)}
                                                    </Tag>
                                                    {(!rule.start || !rule.end || rule.start === "Invalid Date" || rule.end === "Invalid Date") && (
                                                        <Tooltip title="宵禁时间数据格式可能有问题">
                                                            <Tag color="orange" style={{ marginLeft: 8 }}>
                                                                <WarningOutlined /> 时间格式问题
                                                            </Tag>
                                                        </Tooltip>
                                                    )}
                                                </Descriptions.Item>
                                                {/* 原始时间数据 - 仅在开发环境下显示
                                                {process.env.NODE_ENV === "development" && (
                                                    <Descriptions.Item label="原始时间数据">
                                                        <small style={{ color: "#999" }}>
                                                            开始: {rule.start}, 结束: {rule.end}
                                                        </small>
                                                    </Descriptions.Item>
                                                )}
                                                */}
                                                <Descriptions.Item label="例外飞机">
                                                    {rule.exceptions && rule.exceptions.length > 0 ? (
                                                        <Space wrap className="curfew-rules__exceptions">
                                                            {rule.exceptions.map((reg) => (
                                                                <AircraftTag key={reg} registration={reg} />
                                                            ))}
                                                        </Space>
                                                    ) : (
                                                        <Text type="secondary">无例外飞机</Text>
                                                    )}
                                                </Descriptions.Item>
                                            </Descriptions>
                                            {index < arr_curfew_rules.length - 1 && <Divider dashed style={{ margin: "12px 0" }} />}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

export default CurfewRulesViewer;
